/**
 * Time-Phased Workbench — B-mode inputs for the Launch Calendar (Doc 102 §2A).
 * Launch setup (assign profiles to priorities), supply calendar, launch
 * profiles, portfolio rollup, and the placement run.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Calendar,
  Download,
  FileDown,
  Loader2,
  Plus,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import {
  getSupplyCalendar,
  getLaunchProfiles,
  seedBuiltinLaunchProfiles,
  upsertSupplyCalendarRows,
  importSupplyCalendarCsv,
  exportSupplyCalendarCsv,
  supplyCalendarCsvTemplate,
  runTimePhasedPlacement,
  getPortfolioRollupSources,
  savePortfolioRollupSources,
  saveAndApplyPortfolioRollup,
  resolvePortfolioRollupLists,
  ensureScenarioHorizon,
  ensureDefaultSupplyCalendar,
  setupTimePhasedPlanning,
} from '../../../lib/timePhasedPlanningService';
import { engineInputLabel } from '../../../lib/planningEngineService';
import type { LaunchProfile, SupplyCalendarRow } from '../../../lib/timePhasedPlanning.types';
import { resolveSupplyTeamCatalog } from '../../../lib/timePhasedPlanning.types';
import {
  parseSupplyCsv,
  downloadTextFile,
  utilizationHeatClass,
} from '../../../components/planning/timePhased/timePhasedUiShared';
import { TimePhasedModuleGate } from '../../../components/planning/timePhased/TimePhasedModuleGate';
import { LaunchSetupPanel } from '../../../components/planning/timePhased/LaunchSetupPanel';
import { getItemCountState } from '../../../components/collection/types';
import { listPublishedPriorityLists } from '../../../lib/planningService';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { formatOrgUnitPathLabel } from '../../../lib/orgUnitTree';
import { findRollupConflicts, type RollupConflict } from '../../../lib/portfolioRollupConflicts';
import { RollupConflictMap } from '../../../components/planning/timePhased/RollupConflictMap';

type Tab = 'setup' | 'supply' | 'profiles' | 'rollup' | 'run';

const TAB_IDS: Tab[] = ['setup', 'supply', 'profiles', 'rollup', 'run'];

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';


type RollupSourceRow = {
  sourceType: string;
  sourceReferenceId: string;
  label: string;
  org: string;
  orgUnitId: string;
};

function WorkbenchInner() {
  const { projectId, activeScenario, engineInput } = usePlanningEngine();
  const { load: loadResourceTypes, getEnabledTypes } = useResourceTypesStore();
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab');
  const initialTab: Tab =
    tabFromUrl && TAB_IDS.includes(tabFromUrl as Tab) ? (tabFromUrl as Tab) : 'setup';
  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TAB_IDS.includes(t as Tab)) setTab(t as Tab);
  }, [searchParams]);

  const selectTab = (next: Tab) => {
    setTab(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'setup') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  };
  const [supply, setSupply] = useState<SupplyCalendarRow[]>([]);
  const [profiles, setProfiles] = useState<LaunchProfile[]>([]);
  const [publishedLists, setPublishedLists] = useState<{ id: string; name: string }[]>([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [rollupOrgUnitId, setRollupOrgUnitId] = useState('');
  const [rollupSources, setRollupSources] = useState<RollupSourceRow[]>([]);
  const [rollupConflicts, setRollupConflicts] = useState<RollupConflict[]>([]);
  const [rollupConflictsLoading, setRollupConflictsLoading] = useState(false);
  const [rollupConflictsError, setRollupConflictsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [utilization, setUtilization] = useState<{ pct: number; team: string; week: string }[]>([]);

  const scenarioId = activeScenario?.id ?? '';
  const supplyTeams = useMemo(
    () => resolveSupplyTeamCatalog(getEnabledTypes().map((t) => ({ key: t.key, label: t.label }))),
    [getEnabledTypes],
  );
  const orgUnitOptions = useMemo(
    () =>
      [...orgUnitsMap.values()]
        .filter((ou) => ou.active !== false)
        .map((ou) => ({
          id: ou.id,
          label: formatOrgUnitPathLabel(ou, orgUnitsMap),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [orgUnitsMap],
  );

  const load = useCallback(async () => {
    if (!projectId || !scenarioId) return;
    setLoading(true);
    try {
      await loadResourceTypes(projectId);
      void fetchOrgUnits();
      const [cal, prof, sources, lists] = await Promise.all([
        getSupplyCalendar(scenarioId),
        getLaunchProfiles(projectId),
        getPortfolioRollupSources(scenarioId),
        listPublishedPriorityLists(),
      ]);
      setSupply(cal);
      setProfiles(prof);
      setRollupSources(
        sources.map((s) => ({
          sourceType: s.sourceType,
          sourceReferenceId: s.sourceReferenceId,
          label: s.sourceLabel ?? s.sourceReferenceId,
          org: s.owningOrg ?? '',
          orgUnitId: s.owningOrgUnitId ?? '',
        })),
      );
      setPublishedLists((lists.data ?? []).map((l) => ({ id: l.id, name: l.name })));
    } finally {
      setLoading(false);
    }
  }, [projectId, scenarioId, loadResourceTypes, fetchOrgUnits]);

  useEffect(() => {
    void load();
  }, [load]);

  const supplyByTeam = useMemo(() => {
    const map = new Map<string, SupplyCalendarRow[]>();
    for (const row of supply) {
      const list = map.get(row.resourceType) ?? [];
      list.push(row);
      map.set(row.resourceType, list);
    }
    return map;
  }, [supply]);

  const supplyCountState = getItemCountState(supply.length);

  const handleSeedProfiles = async () => {
    if (!projectId) return;
    setSeeding(true);
    try {
      const list = await seedBuiltinLaunchProfiles(projectId);
      setProfiles(list);
      setMessage('Built-in workload templates ready: Standard product launch, Partner campaign, Pricing & packaging.');
    } finally {
      setSeeding(false);
    }
  };

  const handleSeedSupply = async () => {
    if (!scenarioId || !activeScenario || !projectId) return;
    setSeeding(true);
    try {
      const horizon = await ensureScenarioHorizon(scenarioId, activeScenario.planningWeeks);
      const rows = await ensureDefaultSupplyCalendar(
        scenarioId,
        horizon.start,
        horizon.end,
        undefined,
        projectId,
      );
      setSupply(rows);
      setMessage(
        `Supply calendar ready: ${rows.length} team-week rows seeded from Capacity / Planning Defaults. Edit any cell below.`,
      );
    } finally {
      setSeeding(false);
    }
  };

  const handleCsvImport = async (file: File) => {
    if (!scenarioId || !activeScenario) return;
    const text = await file.text();
    const rows = parseSupplyCsv(text);
    if (rows.length === 0) {
      setMessage('No valid rows found. Expected: resource_type, resource_name, bucket_start, bucket_end, supply_hours');
      return;
    }
    await importSupplyCalendarCsv(scenarioId, rows);
    await load();
    setMessage(`Imported ${rows.length} supply calendar rows.`);
  };

  const handleSupplyEdit = async (row: SupplyCalendarRow, hours: number) => {
    await upsertSupplyCalendarRows([
      {
        scenarioId: row.scenarioId,
        resourceType: row.resourceType,
        resourceName: row.resourceName,
        bucketStart: row.bucketStart,
        bucketEnd: row.bucketEnd,
        supplyHours: hours,
      },
    ]);
    setSupply((prev) => prev.map((r) => (r.id === row.id ? { ...r, supplyHours: hours } : r)));
  };

  // The conflict map reads as soon as two lists are in the roll-up, so a
  // collision shows before anything is merged (build contract §2.8).
  const rollupKey = rollupSources.map((s) => `${s.sourceReferenceId}:${s.org}`).join('|');
  useEffect(() => {
    if (!scenarioId || rollupSources.length < 2) {
      setRollupConflicts([]);
      setRollupConflictsError(null);
      return;
    }
    let cancelled = false;
    setRollupConflictsLoading(true);
    setRollupConflictsError(null);
    resolvePortfolioRollupLists(
      rollupSources.map((s, i) => ({
        scenarioId,
        sourceType: s.sourceType,
        sourceReferenceId: s.sourceReferenceId,
        sourceLabel: s.label,
        owningOrg: s.org || undefined,
        owningOrgUnitId: s.orgUnitId || undefined,
        sortOrder: i,
      })),
    )
      .then(({ lists }) => {
        if (cancelled) return;
        setRollupConflicts(
          findRollupConflicts(
            lists.map((l) => ({
              label: l.source.sourceLabel ?? l.source.sourceReferenceId,
              owningOrg: l.source.owningOrg,
              items: l.items,
            })),
          ),
        );
      })
      .catch((e) => {
        if (!cancelled) setRollupConflictsError(e instanceof Error ? e.message : 'Could not read the lists.');
      })
      .finally(() => {
        if (!cancelled) setRollupConflictsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // rollupKey stands for rollupSources' contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, rollupKey]);

  const handleAddRollupSource = () => {
    const list = publishedLists.find((l) => l.id === selectedListId);
    if (!list) return;
    if (rollupSources.some((s) => s.sourceReferenceId === list.id)) {
      setMessage('That published list is already in the rollup.');
      return;
    }
    const ou = rollupOrgUnitId ? orgUnitsMap.get(rollupOrgUnitId) : undefined;
    setRollupSources((prev) => [
      ...prev,
      {
        sourceType: 'published_priority_list',
        sourceReferenceId: list.id,
        label: list.name,
        org: ou ? formatOrgUnitPathLabel(ou, orgUnitsMap) : '',
        orgUnitId: rollupOrgUnitId,
      },
    ]);
    setSelectedListId('');
  };

  const handleSaveRollup = async () => {
    if (!scenarioId) return;
    await savePortfolioRollupSources(
      scenarioId,
      rollupSources.map((s, i) => ({
        scenarioId,
        sourceType: s.sourceType,
        sourceReferenceId: s.sourceReferenceId,
        sourceLabel: s.label,
        owningOrg: s.org || undefined,
        owningOrgUnitId: s.orgUnitId || undefined,
        sortOrder: i,
      })),
    );
    setMessage(`Saved ${rollupSources.length} portfolio rollup source(s).`);
  };

  const handleSaveAndLoadRollup = async () => {
    if (!projectId || !scenarioId) return;
    setRunning(true);
    try {
      const result = await saveAndApplyPortfolioRollup(
        projectId,
        scenarioId,
        rollupSources.map((s, i) => ({
          scenarioId,
          sourceType: s.sourceType,
          sourceReferenceId: s.sourceReferenceId,
          sourceLabel: s.label,
          owningOrg: s.org || undefined,
          owningOrgUnitId: s.orgUnitId || undefined,
          sortOrder: i,
        })),
      );
      const unmatched =
        result.unmatchedLists.length > 0
          ? ` Unmatched: ${result.unmatchedLists.join(', ')}.`
          : '';
      setMessage(
        `Loaded ${result.itemCount} item(s) from published lists → ${result.created} created, ${result.updated} updated priorities.${unmatched}`,
      );
      setTab('setup');
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed to load rollup into scenario');
    } finally {
      setRunning(false);
    }
  };

  const handleRunPlacement = async () => {
    if (!projectId || !scenarioId || !activeScenario) return;
    setRunning(true);
    try {
      await ensureScenarioHorizon(scenarioId, activeScenario.planningWeeks);
      const out = await runTimePhasedPlacement(projectId, scenarioId);
      setUtilization(
        out.bucketUtilization
          .filter((b) => b.utilizationPct >= 60)
          .slice(0, 24)
          .map((b) => ({ pct: b.utilizationPct, team: b.resourceType, week: b.bucketStart })),
      );
      setMessage(
        `Placement run complete: ${out.summary.scheduled} scheduled, ${out.summary.slipped} slipped, ${out.summary.supplyExhausted} supply exhausted. See the Launch Calendar for the timeline.`,
      );
    } finally {
      setRunning(false);
    }
  };

  const handleFullSetup = async () => {
    if (!projectId || !scenarioId || !activeScenario) return;
    setRunning(true);
    try {
      const out = await setupTimePhasedPlanning(
        projectId,
        scenarioId,
        activeScenario.planningWeeks,
      );
      await load();
      setUtilization(
        out.bucketUtilization
          .filter((b) => b.utilizationPct >= 60)
          .slice(0, 24)
          .map((b) => ({ pct: b.utilizationPct, team: b.resourceType, week: b.bucketStart })),
      );
      setMessage(
        `Setup complete: profiles seeded, launch windows assigned, ${out.summary.scheduled} scheduled / ${out.summary.slipped} slipped. Open the Launch Calendar.`,
      );
      setTab('run');
    } finally {
      setRunning(false);
    }
  };

  // Legacy deep-link: Funding & costs is now its own Inputs step.
  if (searchParams.get('tab') === 'funding') {
    return <Navigate to="/planning/engine/funding" replace />;
  }

  if (!activeScenario) {
    return (
      <div className="p-6">
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          Select or create a scenario in the scope bar above to configure time-phased planning.
        </p>
        <Link
          to="/planning/engine/scenarios"
          className="text-sm text-primary-600 hover:underline mt-2 inline-block"
        >
          Go to Scenarios →
        </Link>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'setup', label: 'Launch setup' },
    { id: 'supply', label: 'Supply calendar' },
    { id: 'profiles', label: 'Workload templates' },
    { id: 'rollup', label: 'Portfolio rollup' },
    { id: 'run', label: 'Run & debug' },
  ];

  return (
    <div className="h-full overflow-auto pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Time-Phased Workbench
          </h2>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            Scenario: {activeScenario.name} · {engineInputLabel(engineInput)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/planning/engine/funding"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            Funding &amp; costs
            <ArrowRight className="h-3 w-3" />
          </Link>
          <Link
            to="/planning/launch-calendar"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
          >
            <Calendar className="h-4 w-4" />
            Launch Calendar
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? 'border-primary-600 text-primary-700 dark:text-primary-300'
                : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-ter)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {message && (
        <div className={`${CARD} p-3 mb-4 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]`}>
          {message}
        </div>
      )}

      {!loading && supply.length === 0 && profiles.length === 0 && (
        <div className={`${CARD} p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4`}>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              First-run setup
            </h3>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1">
              Seed workload templates, a shared-team supply calendar, staggered target windows on your
              priorities, and run the first placement — one click.
            </p>
          </div>
          <button
            type="button"
            disabled={running || !scenarioId}
            onClick={() => void handleFullSetup()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 shrink-0"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Set up &amp; run placement
          </button>
        </div>
      )}

      {tab === 'setup' && projectId && (
        <div className="space-y-4">
          {profiles.length === 0 && !loading && (
            <div className={`${CARD} p-4 flex flex-col sm:flex-row sm:items-center gap-3`}>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
                No workload templates yet — seed the built-in templates first, then assign them to
                priorities below.
              </p>
              <button
                type="button"
                disabled={seeding}
                onClick={() => void handleSeedProfiles()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50 shrink-0"
              >
                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Seed built-in profiles
              </button>
            </div>
          )}
          <LaunchSetupPanel
            projectId={projectId}
            scenarioId={scenarioId}
            profiles={profiles}
          />
        </div>
      )}

      {tab !== 'setup' && loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      ) : (
        <>
          {tab === 'supply' && (
            <div className="space-y-4">
              <div className={`${CARD} p-4 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]`}>
                <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  This is the hours dial for Launch Calendar
                </p>
                <p className="mt-1">
                  Week-by-week shared-team supply here drives launch placement heat. Engine{' '}
                  <Link to="/planning/engine/capacity" className="text-primary-600 hover:underline">
                    Resource Budget
                  </Link>{' '}
                  is for viability planning (WU capacity) — editing that page does{' '}
                  <em>not</em> move the Launch Calendar.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] cursor-pointer hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/5">
                  <Upload className="h-4 w-4" />
                  Import CSV
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleCsvImport(f);
                      e.target.value = '';
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() =>
                    downloadTextFile('supply-calendar.csv', exportSupplyCalendarCsv(supply))
                  }
                  disabled={supply.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() =>
                    downloadTextFile(
                      'supply-calendar-template.csv',
                      supplyCalendarCsvTemplate(activeScenario.horizonStartDate ?? undefined),
                    )
                  }
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)]"
                >
                  <FileDown className="h-4 w-4" />
                  Download template
                </button>
                <button
                  type="button"
                  onClick={() => void load()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)]"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload
                </button>
              </div>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                CSV format: resource_type, resource_name, bucket_start, bucket_end, supply_hours
              </p>
              {supplyCountState === 'zero' ? (
                <div className={`${CARD} p-6 text-center text-sm text-[var(--text-ter)] space-y-3`}>
                  <p>No supply rows yet for this scenario.</p>
                  <button
                    type="button"
                    disabled={seeding}
                    onClick={() => void handleSeedSupply()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50"
                  >
                    {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Seed supply from Capacity / Defaults
                  </button>
                  <p className="text-xs">…or import a CSV using the template above.</p>
                </div>
              ) : (
                supplyTeams.map((team) => {
                  const rows = supplyByTeam.get(team.type) ?? [];
                  if (rows.length === 0) return null;
                  return (
                    <SupplyTeamCard
                      key={team.type}
                      label={team.label}
                      rows={rows}
                      onEdit={handleSupplyEdit}
                    />
                  );
                })
              )}
            </div>
          )}

          {tab === 'profiles' && (
            <div className="space-y-4">
              <div className={`${CARD} p-4 flex flex-col sm:flex-row sm:items-center gap-3`}>
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
                  Workload template <strong>library</strong> lives in Settings. Assign profiles to
                  priorities on the Launch setup tab.
                </p>
                <Link
                  to="/prioritization/settings/workload-templates"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white shrink-0"
                >
                  Open Settings → Workload Templates
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
              <button
                type="button"
                disabled={seeding}
                onClick={() => void handleSeedProfiles()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
              >
                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Seed built-in profiles here
              </button>
              {profiles.length === 0 ? (
                <div className={`${CARD} p-6 text-center text-sm text-[var(--text-ter)]`}>
                  No workload templates yet. Seed built-ins or create them in Settings.
                </div>
              ) : (
                profiles.map((p) => (
                  <div key={p.id} className={`${CARD} p-4`}>
                    <div className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{p.name}</div>
                    {p.description && <p className="text-sm text-[var(--text-ter)] mt-1">{p.description}</p>}
                    <ul className="mt-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] space-y-1">
                      {p.phases.map((ph) => (
                        <li key={ph.id}>
                          {ph.label} ({ph.resourceType}): {ph.hours}h, wk {ph.offsetStartWeeks}…{ph.offsetEndWeeks}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === 'rollup' && (
            <div className={`${CARD} p-4 space-y-4`}>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Combine published priority lists into one portfolio scenario. Sources are real
                published lists from Prioritization — not free-text labels.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                  className="flex-1 text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                  aria-label="Published priority list"
                >
                  <option value="">Select published list…</option>
                  {publishedLists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <select
                  value={rollupOrgUnitId}
                  onChange={(e) => setRollupOrgUnitId(e.target.value)}
                  className="sm:w-48 text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                  aria-label="Owning org unit"
                >
                  <option value="">Org unit (optional)</option>
                  {orgUnitOptions.map((ou) => (
                    <option key={ou.id} value={ou.id}>
                      {ou.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!selectedListId}
                  onClick={handleAddRollupSource}
                  className="px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                >
                  Add source
                </button>
              </div>
              {publishedLists.length === 0 && (
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  No published priority lists in this workspace yet. Publish a list in Prioritization
                  first.
                </p>
              )}
              {rollupSources.length > 0 && (
                <ul className="text-sm space-y-1">
                  {rollupSources.map((s, i) => (
                    <li key={s.sourceReferenceId} className="flex justify-between gap-2">
                      <span>
                        {s.label}
                        {s.org ? ` · ${s.org}` : ''}
                        <span className="text-xs text-[var(--text-ter)] ml-2 font-mono">{s.sourceReferenceId.slice(0, 8)}…</span>
                      </span>
                      <button
                        type="button"
                        className="text-xs text-[var(--coral)]"
                        onClick={() => setRollupSources((prev) => prev.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {rollupSources.length >= 2 && (
                <RollupConflictMap
                  conflicts={rollupConflicts}
                  loading={rollupConflictsLoading}
                  error={rollupConflictsError}
                />
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleSaveRollup()}
                  disabled={rollupSources.length === 0}
                  className="px-4 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                >
                  Save sources only
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveAndLoadRollup()}
                  disabled={rollupSources.length === 0 || running}
                  className="px-4 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50"
                >
                  {running ? 'Loading…' : 'Save & load into scenario'}
                </button>
              </div>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Load creates or updates planning priorities from the published list items (rank +
                Item Effort CUs + owning org), then opens Launch setup.
              </p>
            </div>
          )}

          {tab === 'run' && (
            <div className={`${CARD} p-5 space-y-4`}>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Run the placement engine against viability outputs and persist results for the
                Launch Calendar. Priorities without a workload template are placed with assumed
                demand and flagged.
              </p>
              <button
                type="button"
                disabled={running}
                onClick={() => void handleRunPlacement()}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary-600 text-white disabled:opacity-50"
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Run placement engine
              </button>
              {utilization.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)] mb-2">
                    Hot buckets (≥60% util)
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {utilization.map((u, i) => (
                      <div
                        key={i}
                        title={`${u.team} ${u.week}: ${u.pct}%`}
                        className={`h-6 w-6 rounded ${utilizationHeatClass(u.pct)}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SupplyTeamCard({
  label,
  rows,
  onEdit,
}: {
  label: string;
  rows: SupplyCalendarRow[];
  onEdit: (row: SupplyCalendarRow, hours: number) => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 8);
  return (
    <div className={CARD}>
      <div className="px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-between">
        <span className="font-medium text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</span>
        <span className="text-xs text-[var(--text-ter)]">{rows.length} weeks</span>
      </div>
      <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
        {visible.map((row) => (
          <div key={row.id} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
            <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)] font-mono text-base">
              {row.bucketStart}
            </span>
            <input
              type="number"
              min={0}
              value={row.supplyHours}
              onChange={(e) => void onEdit(row, Number(e.target.value))}
              className="w-20 text-right rounded border border-[var(--rule)] dark:border-[var(--rule)] px-2 py-1 font-mono text-base bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
              aria-label={`Hours for ${label} week ${row.bucketStart}`}
            />
            <span className="text-xs text-[var(--text-ter)]">hrs</span>
          </div>
        ))}
      </div>
      {rows.length > 8 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-4 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline text-left"
        >
          {expanded ? 'Show fewer weeks' : `Show all ${rows.length} weeks`}
        </button>
      )}
    </div>
  );
}

export default function TimePhasedWorkbench() {
  return (
    <TimePhasedModuleGate>
      <WorkbenchInner />
    </TimePhasedModuleGate>
  );
}
